import { useEffect, useState } from "react";
import ProductCard from "../components/ProductCard";
import ContactCTA from "../components/ContactCTA";
import SEO from "../components/SEO";
import { useSearchParams } from "react-router-dom";
import { FaFilter, FaSortAmountDown, FaStar, FaSearch, FaTimes, FaUndo } from "react-icons/fa";
import BASE_URL from "../BASEURL";

import AOS from "aos";
import "aos/dist/aos.css";

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesList, setCategoriesList] = useState([]);

  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("");
  const [rating, setRating] = useState("");
  const [search, setSearch] = useState("");
  const backendUrl = BASE_URL;

  const [searchParams] = useSearchParams();
  const categoryFromUrl = decodeURIComponent(
    searchParams.get("category") || ""
  );

  const shopSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Holly Zolly Shop",
    description:
      "Explore our curated collection of authentic Vastu products, yantras, and sacred home decor designed to bring harmony and prosperity.",
    url: "https://hollyzolly.shop/shop",
  };

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 50,
    });
  }, []);

  // Fetch Products
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${backendUrl}/api/product/all`);
        const data = await res.json();

        if (Array.isArray(data)) {
          setProducts(data);
        } else if (Array.isArray(data.products)) {
          setProducts(data.products);
        } else if (Array.isArray(data.data)) {
          setProducts(data.data);
        } else if (Array.isArray(data.data?.products)) {
          setProducts(data.data.products);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fetch Categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/category`);
        const data = await res.json();
        if (data?.success && Array.isArray(data.data)) {
          setCategoriesList(data.data);
        }
      } catch (error) {
        console.error("Failed to load categories:", error);
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    AOS.refresh();
  }, [products]);

  useEffect(() => {
    if (categoryFromUrl && categoryFromUrl !== "all") {
      setCategory(categoryFromUrl);
    } else {
      setCategory("");
    }
  }, [categoryFromUrl]);

  // Filter & Sort Logic
  const filteredProducts = (Array.isArray(products) ? products : [])
    .filter((product) => {
      const productCategoryName =
        product.categoryId?.name ||
        product.category?.name ||
        product.category ||
        (typeof product.categoryId === "string"
          ? categoriesList.find((cat) => cat._id === product.categoryId)?.name
          : "") ||
        "";
      const productCategoryId =
        typeof product.categoryId === "string"
          ? product.categoryId
          : product.categoryId?._id || "";
      
      const selectedCategory = category.toLowerCase().trim();
      const normalizedProductCategory =
        String(productCategoryName || productCategoryId || product.category || "")
          .toLowerCase()
          .trim();
      
      if (category && normalizedProductCategory !== selectedCategory) {
        return false;
      }

      const nameMatch = product.productName || product.name || "";
      if (search && !nameMatch.toLowerCase().includes(search.toLowerCase())) return false;

      if (rating && Number(product.rating || 4.2) < Number(rating)) return false;

      return true;
    })
    .sort((a, b) => {
      const priceA = a.discountPrice || a.price || 0;
      const priceB = b.discountPrice || b.price || 0;
      if (sort === "low") return priceA - priceB;
      if (sort === "high") return priceB - priceA;
      if (sort === "newest") return new Date(b.createdAt) - new Date(a.createdAt);
      if (sort === "oldest") return new Date(a.createdAt) - new Date(b.createdAt);
      return 0;
    });

  const clearAllFilters = () => {
    setCategory("");
    setRating("");
    setSearch("");
    setSort("");
  };

  if (loading) {
    return (
      <>
        <SEO
          title="Shop Vastu Products | Holly Zolly"
          description="Explore our curated collection of authentic Vastu products, sacred yantras, and harmonious home decor at Holly Zolly."
          schema={shopSchema}
        />
        <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-amber-100 animate-pulse"></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-amber-600 animate-spin"></div>
            
          </div>
          <p className="text-lg font-medium text-stone-600 tracking-wide">Unveiling divine collections...</p>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Shop Vastu Products | Holly Zolly"
        description="Explore our curated collection of authentic Vastu products, sacred yantras, and harmonious home decor at Holly Zolly."
        schema={shopSchema}
      />
      <section className="pt-24 pb-20 bg-stone-50 text-stone-800 min-h-screen font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        
        {/* PREMIUM BANNER HERO */}
        <div className="relative rounded-3xl overflow-hidden mb-12 bg-gradient-to-r from-stone-900 via-stone-800 to-amber-950 text-white shadow-2xl py-12 px-6 sm:px-12 text-center sm:text-left border border-amber-900/30" data-aos="fade-down">
          {/* Subtle background graphics */}
          <div className="absolute right-0 bottom-0 opacity-10 text-[20rem] font-bold select-none pointer-events-none transform translate-y-1/4 translate-x-1/4">🕉️</div>
          <div className="absolute top-4 right-4 bg-amber-500/10 border border-amber-500/20 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider text-amber-400">
            Authentic Vedic Selection
          </div>
          
          <div className="max-w-2xl relative z-10">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4 font-serif">
              The Sacred <span className="bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent">Gallery</span>
            </h1>
            <p className="text-stone-300 text-base sm:text-lg mb-8 leading-relaxed max-w-xl">
              Curated spiritual elements, ancient Vastu solutions, and sacred treasures crafted to harmonize energy and elevate your cosmic space.
            </p>

            {/* INTEGRATED MODERN SEARCH BAR */}
            <div className="relative max-w-lg shadow-lg rounded-2xl overflow-hidden bg-white p-1">
              <div className="flex items-center">
                <FaSearch className="text-stone-400 ml-4 shrink-0" />
                <input
                  type="text"
                  placeholder="Search sacred yantras, crystals, & artifacts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-3 pr-10 py-3 text-stone-800 bg-transparent outline-none text-sm placeholder-stone-400"
                />
                {search && ( 
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-4 text-stone-400 hover:text-stone-600 transition-colors"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ACTIVE FILTER PILLS / BREADCRUMB ROW */}
        {(category || rating || search || sort) && (
          <div className="flex flex-wrap items-center gap-2 mb-8 bg-amber-50/50 border border-amber-100 p-3 rounded-2xl" data-aos="fade-in">
            <span className="text-xs font-bold text-amber-900 uppercase tracking-wider mr-2">Active Filters:</span>
            {search && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white border border-amber-200 text-stone-700">
                Search: "{search}"
                <button onClick={() => setSearch("")} className="hover:text-amber-600"><FaTimes size={10} /></button>
              </span>
            )}
            {category && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white border border-amber-200 text-stone-700">
                Category: {category}
                <button onClick={() => setCategory("")} className="hover:text-amber-600"><FaTimes size={10} /></button>
              </span>
            )}
            {rating && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white border border-amber-200 text-stone-700">
                Rating: {rating} ★ +
                <button onClick={() => setRating("")} className="hover:text-amber-600"><FaTimes size={10} /></button>
              </span>
            )}
            {sort && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white border border-amber-200 text-stone-700">
                Sort: {sort === "low" ? "Price: Low to High" : sort === "high" ? "Price: High to Low" : sort === "newest" ? "Newest Arrivals" : "Oldest"}
                <button onClick={() => setSort("")} className="hover:text-amber-600"><FaTimes size={10} /></button>
              </span>
            )}
            <button
              onClick={clearAllFilters}
              className="ml-auto text-xs font-semibold text-amber-700 hover:text-amber-900 hover:underline flex items-center gap-1"
            >
              <FaUndo size={10} /> Clear All
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* HIGH-END MINIMAL SIDEBAR FILTERS */}
          <aside className="lg:col-span-1 space-y-6" data-aos="fade-right">
            
            {/* CATEGORIES CARD */}
            <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
              <h3 className="flex items-center gap-2 font-serif text-lg font-bold text-stone-900 pb-4 mb-4 border-b border-stone-100">
                <FaFilter className="text-amber-600 text-sm" />
                Categories
              </h3>
              <div className="space-y-1 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                <button
                  onClick={() => setCategory("")}
                  className={`w-full text-left px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
                    category === ""
                      ? "bg-amber-600 text-white shadow-sm"
                      : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                  }`}
                >
                  All Collections
                </button>
                {categoriesList.map((cat) => (
                  <button
                    key={cat._id}
                    onClick={() => setCategory(cat.name)}
                    className={`w-full text-left px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
                      category === cat.name
                        ? "bg-amber-600 text-white shadow-sm"
                        : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            {/* SORTING CARD */}
            <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
              <h3 className="flex items-center gap-2 font-serif text-lg font-bold text-stone-900 pb-4 mb-4 border-b border-stone-100">
                <FaSortAmountDown className="text-amber-600 text-sm" />
                Sort Collection
              </h3>
              <div className="space-y-2">
                {[
                  { value: "newest", label: "Newest Arrivals" },
                  { value: "low", label: "Price: Low to High" },
                  { value: "high", label: "Price: High to Low" },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSort(option.value)}
                    className={`w-full text-left px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold border transition-all duration-200 ${
                      sort === option.value
                        ? "border-amber-600 bg-amber-50 text-amber-900"
                        : "border-stone-100 text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* RATING CARD */}
            <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
              <h3 className="flex items-center gap-2 font-serif text-lg font-bold text-stone-900 pb-4 mb-4 border-b border-stone-100">
                <FaStar className="text-amber-600 text-sm" />
                Minimum Rating
              </h3>
              <div className="flex gap-2">
                {[5, 4, 3, 2].map((num) => (
                  <button
                    key={num}
                    onClick={() => setRating(rating === String(num) ? "" : String(num))}
                    className={`flex-1 py-2 rounded-xl border flex items-center justify-center gap-1 text-xs font-bold transition-all duration-200 ${
                      rating === String(num)
                        ? "border-amber-600 bg-amber-50 text-amber-900"
                        : "border-stone-100 text-stone-600 hover:bg-stone-50 hover:text-stone-950"
                    }`}
                  >
                    {num} <FaStar className="text-amber-500" />
                  </button>
                ))}
              </div>
            </div>

          </aside>

          {/* PRODUCTS CATALOG SECTION */}
          <div className="lg:col-span-3" data-aos="fade-left">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm sm:text-base text-stone-500 font-medium">
                Revealing <span className="font-bold text-amber-700">{filteredProducts.length}</span> divine items
              </p>
              <div className="flex items-center gap-1.5 text-xs text-amber-800 font-semibold bg-amber-50 border border-amber-100/50 px-3 py-1 rounded-full">
                <FaStar className="animate-spin text-[10px]" /> Harmonic Energy Verified
              </div>
            </div>

            {filteredProducts.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-3xl border border-stone-200 shadow-sm">
                <div className="text-5xl mb-4 animate-bounce">📿</div>
                <h3 className="text-xl font-bold text-stone-800 mb-2 font-serif">No Sacred Items Found</h3>
                <p className="text-stone-500 text-sm max-w-sm mx-auto mb-6">
                  We couldn't find items matching your criteria. Try adjusting your filters.
                </p>
                <button
                  onClick={clearAllFilters}
                  className="px-6 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl text-sm transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Reset All Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredProducts.map((product, index) => (
                  <div
                    key={product._id}
                    className="h-full transition-transform duration-300"
                    data-aos="fade-up"
                    data-aos-delay={index * 50}
                  >
                    <ProductCard product={product} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      <ContactCTA />
    </section>
    </>
  );
}
